import { EnumInfoDelegaTipo } from '@framework/api-client/api/inquiries-compile/schemas/enumInfoDelegaTipo.ts';
import { useField } from '@framework/form/field/useField.tsx';
import { SelectInput } from '@framework/form/inputs/SelectInput.tsx';
import { SwitchInput } from '@framework/form/inputs/SwitchInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { type IDelegation } from '@framework/frontoffice-submission/module/delegation/IDelegation.ts';
import { RowElementItem } from '@framework/ui/components/row/RowElementItem.tsx';
import { generatePath } from '@framework/ui/core/router/TypedRoute.tsx';
import { Button, Col, Row, Space } from 'antd';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AddressBookPersonButton } from '#features/packages/common-web/src/address-book/components/button/person/AddressBookPersonButton.tsx';
import { DelegationTypeUserTag } from './DelegationTypeUserTag.tsx';
import { type EditModulesDelegationsValues } from './EditModulesDelegationsSchema.ts';

export interface IModulesViewDelegationsInputProps {
  name: string;
  label: string;
  uniqueDelegations: IDelegation[];
  needConfirm: boolean;
}

const TFormItem = FormItem as typeof FormItem<EditModulesDelegationsValues>;

/**
 * Componente di input per la gestione dei dati di delega di visualizzazione.
 * Se i moduli forniti hanno delegati differenti, mostra una maschera
 * che richiede la conferma esplicita per la modifica.
 *
 * Permette l'inserimento di una lista di delegati
 */
export function ModulesViewDelegationsInput(
  props: IModulesViewDelegationsInputProps,
) {
  const { t } = useTranslation();
  const { name, label, uniqueDelegations, needConfirm } = props;

  const enable = useWatch({ name: `view.enable` });
  const isConfirmed = useWatch({ name: `view.confirmUpdate` });
  const confirmField = useField(`view.confirmUpdate`);

  const helpText = t(
    'inserisci-codici-fiscali-o-email-delegati',
    'Inserisci i Codici Fiscali o le E-Mail per gli utenti esteri',
  );

  const selected = useWatch({ name: 'view.delegate' });

  return (
    <Row>
      <Col span={10}>
        <Space>
          <TFormItem
            name="view.enable"
            label={t(
              'modules-delegations.view.abilita-deleghe',
              'Abilita deleghe',
            )}
            layout={{ removeHelpMargin: true, hideVisualLabel: true }}
          >
            <SwitchInput name="view.enable">
              <DelegationTypeUserTag
                delegate={{
                  type: EnumInfoDelegaTipo.VISUALIZZAZIONE,
                  userName: label,
                }}
              />
            </SwitchInput>
          </TFormItem>
        </Space>
      </Col>
      <Col span={14}>
        {enable &&
          (needConfirm ? (
            <>
              <RowElementItem colSpan={4}>
                <TFormItem name="view.delegate" label={label} help={helpText}>
                  <Space.Compact size="large" block>
                    <SelectInput
                      name="view.delegate"
                      disabled={!isConfirmed}
                      placeholder={
                        isConfirmed
                          ? undefined
                          : t(
                              'delegati-differenti-o-mancanti',
                              'I moduli presentano delegati differenti o mancanti',
                            )
                      }
                      mode="tags"
                    />
                    <AddressBookPersonButton
                      idFieldName="view.delegate"
                      addressBookUrl={generatePath('/rubrica')}
                      onSelect={(record, close, setValue) => {
                        setValue((ids: string[]) => [
                          ...new Set([...ids, record.codiceFiscale]),
                        ]);
                      }}
                      selected={selected}
                    />
                  </Space.Compact>
                </TFormItem>
              </RowElementItem>

              <Button
                block
                size="large"
                type={isConfirmed ? 'default' : 'primary'}
                onClick={() => confirmField.setValue(!isConfirmed)}
              >
                {isConfirmed
                  ? t('annulla-modifiche', 'Annulla modifiche')
                  : t('modifica-tutti', 'Modifica tutti')}
              </Button>

              {/* <ModulesPreviousDelegationsSummary
                delegations={uniqueDelegations}
              /> */}
            </>
          ) : (
            <TFormItem name="view.delegate" label={label} help={helpText}>
              <Space.Compact size="large" block>
                <SelectInput name="view.delegate" mode="tags" />
                <AddressBookPersonButton
                  idFieldName="view.delegate"
                  addressBookUrl={generatePath('/rubrica')}
                  onSelect={(record, close, setValue) => {
                    setValue((ids: string[]) => [
                      ...new Set([...ids, record.codiceFiscale ?? record.pec]),
                    ]);
                  }}
                  selected={selected}
                />
              </Space.Compact>
            </TFormItem>
          ))}
      </Col>
    </Row>
  );
}
